import React, { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectUserRole } from "../../../core/data/slice/authSlice";
import { useCourse } from "../../configuration/course/hooks/useCourse";
import { useCenter } from "../../peoples/centers/hooks/useCenter";
import Table from "../../../core/common/dataTable/index";
import { all_routes } from "../../router/all_routes";
import CommonSelect, { Option } from "../../../core/common/commonSelect";
import { useInvoiceManagement, FeeItem } from "../hooks/useInvoiceManagement";
import { formatDateToYMD } from "../../../core/utils/dateFormatter";

const CollectFees = () => {
  const {
    fees,
    loading,
    error,
    selectedCenter,
    setSelectedCenter,
    fetchFees,
    getFilteredFees,
    updateFilters,
    paginationData,
    filters,
    isAllMode,
    setIsAllMode,
    resetLazyState,
  } = useInvoiceManagement();

  const userRole = useSelector(selectUserRole);
  const { getCourseState } = useCourse({ size: 100 });
  const { courses } = getCourseState();
  const allCourseOptions = courses.map((c) => ({
    value: c.id.toString(),
    label: c.name,
  }));

  const { useCenterList } = useCenter({ skip: true });
  const { data: centerResponse } = useCenterList();
  const allCenters = centerResponse?.data?.items || [];
  const allCenterOptions = allCenters.map((c) => ({
    value: c.id.toString(),
    label: c.name,
  }));

  const [tempFilters, setTempFilters] = useState({
    course_id: filters.course_id,
    center_id: filters.center_id,
    fee_type: filters.fee_type,
    status: filters.status,
    from_due_date: filters.from_due_date,
    to_due_date: filters.to_due_date,
  });

  const [recordsPerPage, setRecordsPerPage] = useState(filters.size || 10);
  const [pageNumber, setPageNumber] = useState(filters.page || 1);
  const [search, setSearch] = useState(filters.search || "");

  const dropdownMenuRef = useRef<HTMLDivElement | null>(null);

  // Fetch fees on component mount
  useEffect(() => {
    fetchFees();
  }, [fetchFees]);

  // Keep local state in sync with filter changes
  useEffect(() => {
    setPageNumber(filters.page);
    setRecordsPerPage(filters.size);
    setSearch(filters.search);
  }, [filters]);

  const handleApplyClick = () => {
    if (dropdownMenuRef.current) {
      dropdownMenuRef.current.classList.remove("show");
    }
  };

  const handleSearchChange = (text: string) => {
    setSearch(text);
    updateFilters({ search: text });
  };

  // Modified page change handler - directly update filters
  const handlePageChange = (page: number, pageSize?: number) => {
    // Update local state
    setPageNumber(page);
    if (pageSize) {
      const total = paginationData.totalItems || 0;
      const isSelectingAll = total > 0 && pageSize >= total;

      if (isSelectingAll) {
        setIsAllMode(true);
        resetLazyState(getFilteredFees() || []);
        setRecordsPerPage(pageSize);
        return;
      }

      if (isAllMode) {
        setIsAllMode(false);
        resetLazyState();
      }
      setRecordsPerPage(pageSize);
    }

    // Update filters for API request
    if (pageSize) {
      updateFilters({ page, size: pageSize });
    } else {
      updateFilters({ page });
    }
  };

  const filteredFees = getFilteredFees();

  const feeTypeOptions = [
    { value: "registrationfee", label: "Registration Fee" },
    { value: "coursefee", label: "Course Fee" },
  ];

  const statusOptions = [
    { value: "pending", label: "Pending" },
    { value: "partial", label: "Partial" },
    { value: "completed", label: "Completed" },
  ];

  const findOptionByValue = (options: any[], value: string | undefined) => {
    if (!value) return null;
    return options.find((option) => option.value === value) || null;
  };

  const columns = [
    {
      title: "Invoice Number",
      dataIndex: "id",
      render: (text: number, record: FeeItem) => (
        <Link
          to={`${all_routes.studentFeesInvoice
            .replace(":id", record.student_id.toString())
            .replace(":invoiceId", text.toString())}`}
          className="link-primary"
        >
          {`INV-${text}`}
        </Link>
      ),
    },
    {
      title: "Student Name",
      dataIndex: "student",
      render: (student: any, record: FeeItem) => (
        <Link
          to={all_routes.studentDetail.replace(":id", record.student_id.toString())}
          className="link-primary"
        >
          {student?.name || "N/A"}
        </Link>
      ),
    },
    {
      title: "Fee Type",
      dataIndex: "type",
      render: (text: string) => {
        let displayType = "Unknown";
        let colorClass = "info";

        if (text === "registrationfee") {
          displayType = "Registration Fee";
          colorClass = "primary";
        } else if (text === "coursefee") {
          displayType = "Course Fee";
          colorClass = "success";
        } else if (text) {
          displayType = text
            .replace(/([A-Z])/g, " $1")
            .replace(/^./, (str) => str.toUpperCase())
            .replace(/fee$/, "Fee");
        }

        return (
          <span
            className={`badge badge-soft-${colorClass} d-inline-flex align-items-center`}
          >
            {displayType}
          </span>
        );
      },
    },
    {
      title: "Total Amount",
      dataIndex: "amount",
      render: (amount: string) => `${parseFloat(amount || "0").toFixed(2)}`,
    },
    {
      title: "Payable Amount",
      dataIndex: "payable",
      render: (amount: string) => `${parseFloat(amount || "0").toFixed(2)}`,
    },
    {
      title: "Discount Amount",
      dataIndex: "discount_value",
      render: (discountValue: string | undefined, record: FeeItem) => {
        if (!discountValue) return "0.00";
        const totalAmount = parseFloat(record.amount || "0");
        if (record.discount_type === "percent") {
          const percentValue = parseFloat(discountValue);
          const discountAmount = (percentValue / 100) * totalAmount;
          return `${discountAmount.toFixed(2)}`;
        } else {
          return `${parseFloat(discountValue).toFixed(2)}`;
        }
      },
    },
    {
      title: "Due Date",
      dataIndex: "due_date",
      render: (date: string) => formatDateToYMD(date),
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (text: string | undefined) => (
        <>
          {text === "completed" ? (
            <span className="badge badge-soft-success d-inline-flex align-items-center">
              <i className="ti ti-circle-filled fs-5 me-1"></i>
              {text}
            </span>
          ) : (
            <span className="badge badge-soft-warning d-inline-flex align-items-center">
              <i className="ti ti-circle-filled fs-5 me-1"></i>
              {typeof text === "string" ? text : "pending"}
            </span>
          )}
        </>
      ),
    },
    {
      title: "Action",
      render: (record: FeeItem) => (
        <>
          <div className="d-flex align-items-center">
            <div className="dropdown">
              <Link
                to="#"
                className="btn btn-white btn-icon btn-sm d-flex align-items-center justify-content-center rounded-circle p-0"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                <i className="ti ti-dots-vertical fs-14" />
              </Link>
              <ul className="dropdown-menu dropdown-menu-right p-3">
                {record.status !== "completed" && (
                  <li>
                    <Link
                      className="dropdown-item rounded-1"
                      to={`${all_routes.studentFeesDiscount
                        .replace(":id", record.student_id.toString())
                        .replace(":invoiceId", record.id.toString())}`}
                    >
                      <i className="ti ti-discount-2 me-2" />
                      Apply Discount
                    </Link>
                  </li>
                )}
                <li>
                  <Link
                    className="dropdown-item rounded-1"
                    to={`${all_routes.studentFeesInvoice
                      .replace(":id", record.student_id.toString())
                      .replace(":invoiceId", record.id.toString())}`}
                  >
                    <i className="ti ti-eye me-2" />
                    View Invoice
                  </Link>
                </li>
              </ul>
            </div>
          </div>
        </>
      ),
    },
  ];

  return (
    <>
      {/* Page Wrapper */}
      <div className="page-wrapper">
        <div className="content">
          {/* Page Header */}
          <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
            <div className="my-auto mb-2">
              <h3 className="page-title mb-1">Fee Collection</h3>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={all_routes.adminDashboard}>Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item active">Fee Collection</li>
                </ol>
              </nav>
            </div>
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
              <div className="mb-2">
                <Link
                  to={all_routes.generateInvoices}
                  className="btn btn-primary d-flex align-items-center"
                >
                  <i className="ti ti-square-rounded-plus me-2" />
                  Generate Invoices
                </Link>
              </div>
            </div>
          </div>
          {/* /Page Header */}

          {/* Fees Table */}
          <div className="card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
              <h4 className="mb-3">Fees Collection List</h4>
              <div className="d-flex align-items-center flex-wrap">
                {/* <div className="input-icon-start mb-3 me-2 position-relative">
                  <PredefinedDateRanges />
                </div> */}

                {/* Active Filters Chips */}
                <div className="d-flex align-items-center flex-wrap gap-2 mb-3 me-3">
                  {filters.from_due_date && (
                    <span className="badge badge-soft-info px-2 py-1 d-flex align-items-center">
                      Due From: {filters.from_due_date}
                    </span>
                  )}
                  {filters.to_due_date && (
                    <span className="badge badge-soft-info px-2 py-1 d-flex align-items-center">
                      Due To: {filters.to_due_date}
                    </span>
                  )}
                  {filters.course_id && (
                    <span className="badge badge-soft-info px-2 py-1 d-flex align-items-center">
                      Course: {allCourseOptions.find((c) => c.value === filters.course_id?.toString())?.label}
                    </span>
                  )}
                  {filters.status && filters.status !== "pending" && (
                    <span className="badge badge-soft-info px-2 py-1 d-flex align-items-center">
                      Status: {filters.status.charAt(0).toUpperCase() + filters.status.slice(1)}
                    </span>
                  )}
                </div>

                {/* Add Filter Dropdown */}
                <div className="dropdown mb-3 me-2">
                  <Link
                    to="#"
                    className="btn btn-outline-light bg-white dropdown-toggle"
                    data-bs-toggle="dropdown"
                    data-bs-auto-close="outside"
                    onClick={() => {
                      setTempFilters({
                        course_id: filters.course_id,
                        center_id: filters.center_id,
                        fee_type: filters.fee_type,
                        status: filters.status,
                        from_due_date: filters.from_due_date,
                        to_due_date: filters.to_due_date,
                      });
                    }}
                  >
                    <i className="ti ti-filter me-2" />
                    Filter
                  </Link>
                  <div
                    className="dropdown-menu drop-width"
                    ref={dropdownMenuRef}
                  >
                    <form>
                      <div className="d-flex align-items-center border-bottom p-3">
                        <h4>Filter</h4>
                      </div>
                      <div className="p-3 border-bottom">
                        <div className="row">
                          <div className={userRole === "superadmin" ? "col-md-6" : "col-md-12"}>
                            <div className="mb-3">
                              <label className="form-label">Course</label>
                              <CommonSelect
                                className="select"
                                options={allCourseOptions}
                                onChange={(option: Option | null) =>
                                  setTempFilters({
                                    ...tempFilters,
                                    course_id: option
                                      ? parseInt(option.value)
                                      : undefined,
                                  })
                                }
                                value={findOptionByValue(
                                  allCourseOptions,
                                  tempFilters.course_id?.toString()
                                )}
                              />
                            </div>
                          </div>
                          {userRole === "superadmin" && (
                            <div className="col-md-6">
                              <div className="mb-3">
                                <label className="form-label">Center</label>
                                <CommonSelect
                                  className="select"
                                  options={allCenterOptions}
                                  onChange={(option: Option | null) => {
                                    setTempFilters({
                                      ...tempFilters,
                                      center_id: option ? parseInt(option.value) : undefined,
                                    });
                                  }}
                                  value={findOptionByValue(
                                    allCenterOptions,
                                    tempFilters.center_id?.toString()
                                  )}
                                />
                              </div>
                            </div>
                          )}
                        </div>
                        <div className="row">
                          <div className="col-md-6">
                            <div className="mb-0">
                              <label className="form-label">Fee Type</label>
                              <CommonSelect
                                className="select"
                                options={feeTypeOptions}
                                onChange={(option: Option | null) =>
                                  setTempFilters({
                                    ...tempFilters,
                                    fee_type: option?.value || undefined,
                                  })
                                }
                                value={findOptionByValue(
                                  feeTypeOptions,
                                  tempFilters.fee_type
                                )}
                              />
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="mb-0">
                              <label className="form-label">Status</label>
                              <CommonSelect
                                className="select"
                                options={statusOptions}
                                onChange={(option: Option | null) =>
                                  setTempFilters({
                                    ...tempFilters,
                                    status: option?.value || undefined,
                                  })
                                }
                                value={findOptionByValue(
                                  statusOptions,
                                  tempFilters.status
                                )}
                              />
                            </div>
                          </div>
                        </div>
                        <div className="row mt-3">
                          <div className="col-md-6">
                            <div className="mb-0">
                              <label className="form-label">Due Date From</label>
                              <input
                                type="date"
                                className="form-control"
                                value={tempFilters.from_due_date || ""}
                                onChange={(e) =>
                                  setTempFilters({
                                    ...tempFilters,
                                    from_due_date: e.target.value || undefined,
                                  })
                                }
                              />
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="mb-0">
                              <label className="form-label">Due Date To</label>
                              <input
                                type="date"
                                className="form-control"
                                value={tempFilters.to_due_date || ""}
                                onChange={(e) =>
                                  setTempFilters({
                                    ...tempFilters,
                                    to_due_date: e.target.value || undefined,
                                  })
                                }
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="p-3 d-flex align-items-center justify-content-end">
                        <Link
                          to="#"
                          className="btn btn-light me-3"
                          onClick={() => {
                            setTempFilters({
                              center_id: undefined,
                              course_id: undefined,
                              fee_type: undefined,
                              status: "pending",
                              from_due_date: undefined,
                              to_due_date: undefined,
                            });
                            setSelectedCenter("all");
                            updateFilters({
                              center_id: undefined,
                              course_id: undefined,
                              fee_type: undefined,
                              status: "pending",
                              from_due_date: undefined,
                              to_due_date: undefined,
                              search: "",
                              page: 1,
                            });
                            setSearch("");
                            handleApplyClick();
                          }}
                        >
                          Reset
                        </Link>
                        <Link
                          to="#"
                          className="btn btn-primary"
                          onClick={() => {
                            if (tempFilters.center_id !== undefined) {
                              setSelectedCenter(tempFilters.center_id.toString());
                            } else {
                              setSelectedCenter("all");
                            }
                            updateFilters({
                              center_id: tempFilters.center_id,
                              course_id: tempFilters.course_id,
                              fee_type: tempFilters.fee_type,
                              status: tempFilters.status,
                              from_due_date: tempFilters.from_due_date,
                              to_due_date: tempFilters.to_due_date,
                              page: 1,
                            });
                            handleApplyClick();
                          }}
                        >
                          Apply
                        </Link>
                      </div>
                    </form>
                  </div>
                </div>


              </div>
            </div>
            <div className="card-body p-0 py-3">
              {loading ? (
                <div className="text-center">Loading fees data...</div>
              ) : error ? (
                <div className="alert alert-danger m-3">
                  Error loading fees: {error.toString()}
                </div>
              ) : (
                <Table
                  dataSource={filteredFees}
                  columns={columns}
                  Selection={false}
                  onSearchChange={handleSearchChange}
                  pagination={{
                    current: pageNumber,
                    pageSize: recordsPerPage,
                    total: paginationData.totalItems || 0,
                    onChange: handlePageChange,
                    pageSizeOptions: ["All", 10, 20, 30],
                  }}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CollectFees;
